import { useCallback, useEffect, useRef } from "react";
import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../../store/authStore";
import { useRabbitMQ, type RBEvent } from "../../hooks/useRabbitMQ";
import { useCastQueueStore } from "../../store/castQueueStore";
import { useStreamStatusStore } from "../../store/streamStatusStore";
import {
  getActiveStreamSummary,
  type ActiveStreamSummary,
} from "../../api/stream";

/**
 * The summary, or null when the backend has not shipped the endpoint yet. A 404
 * is not an error: the indicator runs on events alone for the rest of the
 * session, with no toast. Anything else is a real failure, and react-query
 * retries it.
 */
async function fetchActiveSummary(): Promise<ActiveStreamSummary | null> {
  try {
    return await getActiveStreamSummary();
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) return null;
    throw err;
  }
}

/**
 * The app's one consumer of stream events. Renders nothing.
 *
 * Mounted once in `AppRoutes`, outside both `<Routes>` trees, for the same
 * reason as `PipHost`: `ProtectedLayout` mounts twice while a post modal is
 * open, and a second consumer would apply every event twice.
 */
export default function StreamStatusBridge() {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  // Not a ["userChannel"] query: this component also renders on /login, where
  // that request would 401 and the refresh interceptor would redirect.
  // ProtectedLayout copies the channel into the auth store.
  const channel = useAuthStore((s) => s.channel);
  const applyEvent = useStreamStatusStore((s) => s.applyEvent);
  const reset = useStreamStatusStore((s) => s.reset);
  const wasAuthenticated = useRef(isAuthenticated);
  const userId = useAuthStore((s) => s.userId);
  const setLiveByGc = useStreamStatusStore((s) => s.setLiveByGc);

  // Cold start: a stream that went live before the app loaded sends no event.
  // Keyed on the user, so signing in as someone else asks again.
  const { data: summary } = useQuery({
    queryKey: ["activeStreamSummary", userId],
    queryFn: fetchActiveSummary,
    // userId is part of the query key: fetching before it is known would run
    // (and cache) a ["activeStreamSummary", null] query that a moment later,
    // once userId arrives, is simply abandoned for a fresh key.
    enabled: isAuthenticated && userId != null,
    // Once per session — events keep liveByGc current after that.
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!summary) return;
    setLiveByGc(
      Object.fromEntries(summary.liveGcIds.map((gcId) => [gcId, true])),
    );
  }, [summary, setLiveByGc]);

  // Keyed on the sign-out transition, not on unmount — the same pattern
  // PipHost uses, for the same reason.
  useEffect(() => {
    if (wasAuthenticated.current && !isAuthenticated) {
      reset();
      useCastQueueStore.getState().reset();
      // staleTime: Infinity means this query otherwise survives sign-out. Re-
      // login as the same user reuses the query key, so without clearing it
      // here, the cached object never refetches and the effect below (which
      // only reacts to a changed `summary`) never re-applies it to liveByGc,
      // which reset() just emptied.
      queryClient.removeQueries({ queryKey: ["activeStreamSummary"] });
    }
    wasAuthenticated.current = isAuthenticated;
  }, [isAuthenticated, reset, queryClient]);

  const onEvent = useCallback(
    (event: RBEvent) => {
      applyEvent(event);
      if (event.action === "STC_EVENT" || event.action === "GC_EVENT") useCastQueueStore.getState().fromEvent(event.metadata);
      // The store refetches slots on BY_STC_CHANGED itself, but cannot reach
      // react-query to invalidate the chat settings that change with it.
      if (event.action !== "STC_EVENT" || event.metadata.slot != null) return;
      const byStcChanged = event.metadata.activities?.some(
        (activity) => activity.behavior === "BY_STC_CHANGED",
      );
      if (!byStcChanged) return;
      const { stcId, gcId } = useStreamStatusStore.getState();
      if (stcId == null || gcId == null) return;
      void queryClient.invalidateQueries({
        queryKey: ["gcChatSettings", stcId, gcId],
      });
    },
    [applyEvent, queryClient],
  );

  useRabbitMQ(isAuthenticated ? channel : null, onEvent, "StreamStatusBridge");

  return null;
}
